import {
  FinancialApplication,
  FinancialPerson,
} from '@/api/financial/application/read-application'
import { removeNonNumeric } from '@/helpers'
import { isMarried } from '@/pages/credit/journeys/allowance/allowance-flow'
import { BackerRole, InternshipStep } from './internship-flow'

export const digits = removeNonNumeric

/** Responsável financeiro ou avalista: o mesmo cadastro nos dois papéis. */
export type BackerForm = {
  key: string
  name: string
  relationship: string
  tax_id: string
  birthdate: string
  gender: string
  email: string
  phone: string
  marital: string
  spouse_name: string
  spouse_tax_id: string
  spouse_income: string
  occupation_type: string
  occupation_description: string
  income: string
  education_level: string
  zip_code: string
  street: string
  district: string
  number: string
  complement: string
  municipality_id: string
  address_state: string
  politically_exposed: string
  us_person: string
}

export type InternshipForm = {
  collegeKey: string
  tuition: string
  graduation: string
  startSemester: number
  paidTuitions: number
  hasDebt: boolean
  debt: string
  birthdate: string
  gender: string
  zip_code: string
  street: string
  district: string
  number: string
  complement: string
  municipality_id: string
  address_state: string
  marital: string
  spouse_name: string
  spouse_tax_id: string
  politically_exposed: string
  us_person: string
  high_school_type: string
  academic_award: string
  academic_award_description: string
  course_locked: string
  course_locked_reason: string
  plans: string[]
  plans_other: string
  residency_timing: string
  payment_sources: string[]
  payment_story: string
  financing_reason: string
  /** '' até a pessoa escolher entre "Eu mesmo" e "Outra pessoa". */
  selfResponsible: '' | 'yes' | 'no'
  self_income: string
  /** Responsáveis que a pessoa disse que não serão avalistas. */
  guarantorDeclined: string[]
  editing: BackerRole | ''
  backer: BackerForm
  scr: boolean
}

export function emptyBacker(): BackerForm {
  return {
    key: '',
    name: '',
    relationship: '',
    tax_id: '',
    birthdate: '',
    gender: '',
    email: '',
    phone: '',
    marital: '',
    spouse_name: '',
    spouse_tax_id: '',
    spouse_income: '',
    occupation_type: '',
    occupation_description: '',
    income: '',
    education_level: '',
    zip_code: '',
    street: '',
    district: '',
    number: '',
    complement: '',
    municipality_id: '',
    address_state: '',
    politically_exposed: '',
    us_person: '',
  }
}

export function emptyForm(): InternshipForm {
  return {
    collegeKey: '',
    tuition: '',
    graduation: '',
    startSemester: 0,
    paidTuitions: 0,
    hasDebt: false,
    debt: '',
    birthdate: '',
    gender: '',
    zip_code: '',
    street: '',
    district: '',
    number: '',
    complement: '',
    municipality_id: '',
    address_state: '',
    marital: '',
    spouse_name: '',
    spouse_tax_id: '',
    politically_exposed: '',
    us_person: '',
    high_school_type: '',
    academic_award: '',
    academic_award_description: '',
    course_locked: '',
    course_locked_reason: '',
    plans: [],
    plans_other: '',
    residency_timing: '',
    payment_sources: [],
    payment_story: '',
    financing_reason: '',
    selfResponsible: '',
    self_income: '',
    guarantorDeclined: [],
    editing: '',
    backer: emptyBacker(),
    scr: false,
  }
}

function yesNo(value: boolean | null | undefined) {
  if (value == null) return ''
  return value ? 'yes' : 'no'
}

function text(value: string | number | null | undefined) {
  if (value == null) return ''
  return String(value)
}

export type ApplicationLink =
  FinancialApplication['application_persons'][number]

export function studentLink(application: FinancialApplication) {
  return application.application_persons.find((link) =>
    link.person_types.includes('student'),
  )
}

/** Responsáveis e avalistas que não são o próprio estudante. */
export function backerLinks(
  application: FinancialApplication,
  role: BackerRole,
) {
  const type = role === 'r' ? 'financial_responsible' : 'guarantor'
  return application.application_persons.filter(
    (link) =>
      link.person_types.includes(type) &&
      !link.person_types.includes('student'),
  )
}

export function backerFromPerson(
  person: FinancialPerson,
  relationship?: string | null,
): BackerForm {
  const address = person.address[0]
  return {
    ...emptyBacker(),
    key: person.key,
    name: person.name ?? '',
    relationship: relationship ?? '',
    tax_id: person.tax_id ?? '',
    birthdate: person.birthdate?.slice(0, 10) ?? '',
    gender: person.gender ?? '',
    email: person.email ?? '',
    phone: person.phone ?? '',
    marital: person.marital_status ?? '',
    spouse_name: person.spouse_name ?? '',
    spouse_tax_id: person.spouse_tax_id ?? '',
    spouse_income: text(person.spouse_monthly_income),
    occupation_type: person.occupation_type ?? '',
    occupation_description: person.occupation_description ?? '',
    income: text(person.monthly_income),
    education_level: person.education_level ?? '',
    zip_code: address?.zip_code ?? '',
    street: address?.street ?? '',
    district: address?.district ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    municipality_id: address ? String(address.municipality_id) : '',
    address_state: address?.municipality?.state ?? '',
    politically_exposed: yesNo(person.politically_exposed),
    us_person: yesNo(person.us_person),
  }
}

export function formFromApplication(
  application: FinancialApplication,
  graduation: string | null | undefined,
): InternshipForm {
  const link = studentLink(application)
  const person = link?.person
  const address = person?.address[0]
  const simulation = application.simulation
  const self = link?.person_types.includes('financial_responsible')
  const responsibles = backerLinks(application, 'r')
  const debt = Number(simulation?.college_debt ?? 0)
  return {
    ...emptyForm(),
    collegeKey: application.college?.key ?? '',
    tuition: text(person?.monthly_tuition),
    graduation: graduation?.slice(0, 7) ?? '',
    startSemester: simulation?.start_semester ?? 0,
    paidTuitions: simulation?.paid_tuitions ?? 0,
    hasDebt: debt > 0,
    debt: debt > 0 ? String(debt) : '',
    birthdate: person?.birthdate?.slice(0, 10) ?? '',
    gender: person?.gender ?? '',
    zip_code: address?.zip_code ?? '',
    street: address?.street ?? '',
    district: address?.district ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    municipality_id: address ? String(address.municipality_id) : '',
    address_state: address?.municipality?.state ?? '',
    marital: person?.marital_status ?? '',
    spouse_name: person?.spouse_name ?? '',
    spouse_tax_id: person?.spouse_tax_id ?? '',
    politically_exposed: yesNo(person?.politically_exposed),
    us_person: yesNo(person?.us_person),
    high_school_type: person?.high_school_type ?? '',
    academic_award: yesNo(person?.academic_award),
    academic_award_description: person?.academic_award_description ?? '',
    course_locked: yesNo(person?.course_locked),
    course_locked_reason: person?.course_locked_reason ?? '',
    plans: person?.post_graduation_plans ?? [],
    plans_other: person?.post_graduation_plans_other ?? '',
    residency_timing: person?.residency_timing ?? '',
    payment_sources: person?.tuition_payment_sources ?? [],
    payment_story: person?.tuition_payment_story ?? '',
    financing_reason: person?.financing_reason ?? '',
    selfResponsible: self ? 'yes' : responsibles.length ? 'no' : '',
    self_income: text(person?.monthly_income),
    scr: Boolean(person?.authorized_scr),
  }
}

function addressPayload(source: {
  zip_code: string
  street: string
  number: string
  complement: string
  district: string
  municipality_id: string
}) {
  return {
    address: {
      zip_code: digits(source.zip_code),
      street: source.street,
      number: source.number,
      complement: source.complement,
      district: source.district,
      municipality_id: Number(source.municipality_id),
    },
  }
}

function civilPayload(source: {
  marital: string
  spouse_name: string
  spouse_tax_id: string
  politically_exposed: string
  us_person: string
}) {
  const married = isMarried(source.marital)
  return {
    marital_status: source.marital,
    politically_exposed: source.politically_exposed === 'yes',
    us_person: source.us_person === 'yes',
    spouse_name: married ? source.spouse_name : null,
    spouse_tax_id: married ? digits(source.spouse_tax_id) : null,
  }
}

/** O que cada etapa do estudante grava na pessoa do financial. */
export function studentPayloadForStep(
  step: InternshipStep,
  form: InternshipForm,
): Record<string, unknown> | null {
  switch (step) {
    case 'eu':
      return { birthdate: form.birthdate, gender: form.gender }
    case 'end':
      return addressPayload(form)
    case 'civil':
      return civilPayload(form)
    case 'traj':
      return {
        high_school_type: form.high_school_type,
        academic_award: form.academic_award === 'yes',
        academic_award_description:
          form.academic_award === 'yes'
            ? form.academic_award_description
            : null,
        course_locked: form.course_locked === 'yes',
        course_locked_reason:
          form.course_locked === 'yes' ? form.course_locked_reason : null,
      }
    case 'planos':
      return {
        post_graduation_plans: form.plans,
        post_graduation_plans_other: form.plans.includes('other')
          ? form.plans_other
          : null,
        residency_timing: form.residency_timing,
      }
    case 'fin':
      return {
        tuition_payment_sources: form.payment_sources,
        tuition_payment_story: form.payment_story || null,
        financing_reason: form.financing_reason,
      }
    default:
      return null
  }
}

/** O que cada etapa do cadastro de responsável ou avalista grava na pessoa. */
export function backerPayloadForStep(
  index: number,
  backer: BackerForm,
): Record<string, unknown> {
  switch (index) {
    case 1:
      return {
        name: backer.name,
        tax_id: digits(backer.tax_id),
        birthdate: backer.birthdate,
        gender: backer.gender,
      }
    case 2:
      return {
        email: backer.email,
        phone: digits(backer.phone),
        ...civilPayload(backer),
        spouse_monthly_income:
          isMarried(backer.marital) && backer.spouse_income
            ? Number(backer.spouse_income)
            : null,
      }
    case 3:
      return {
        occupation_type: backer.occupation_type,
        occupation_description: backer.occupation_description || null,
        monthly_income: Number(backer.income),
        education_level: backer.education_level,
      }
    default:
      return {
        ...addressPayload(backer),
        politically_exposed: backer.politically_exposed === 'yes',
        us_person: backer.us_person === 'yes',
      }
  }
}

function ageAt(birthdate: string, now: Date) {
  const birth = new Date(`${birthdate}T12:00:00`)
  if (Number.isNaN(birth.getTime())) return -1
  let age = now.getFullYear() - birth.getFullYear()
  const before =
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  if (before) age -= 1
  return age
}

/** Responsável: maior de idade. Avalista: de 18 a 74 anos. */
export function backerAgeOk(
  role: BackerRole,
  birthdate: string,
  now = new Date(),
) {
  const age = ageAt(birthdate, now)
  return age >= 18 && (role === 'r' || age <= 74)
}

export function isBackerStepValid(
  role: BackerRole,
  index: number,
  backer: BackerForm,
  minIncome: number,
) {
  switch (index) {
    case 1:
      return Boolean(
        backer.name.trim().split(/\s+/).length >= 2 &&
          backer.relationship &&
          digits(backer.tax_id).length === 11 &&
          backerAgeOk(role, backer.birthdate) &&
          backer.gender,
      )
    case 2:
      return Boolean(
        /\S+@\S+\.\S+/.test(backer.email) &&
          digits(backer.phone).length >= 10 &&
          backer.marital &&
          (!isMarried(backer.marital) ||
            (backer.spouse_name && digits(backer.spouse_tax_id).length === 11)),
      )
    case 3:
      return Boolean(
        backer.occupation_type &&
          backer.income !== '' &&
          backer.education_level &&
          (role === 'r' || Number(backer.income) >= minIncome),
      )
    default:
      return Boolean(
        backer.zip_code &&
          backer.street &&
          backer.number &&
          backer.district &&
          backer.municipality_id &&
          backer.politically_exposed &&
          backer.us_person,
      )
  }
}

export function isStepValid(
  step: InternshipStep,
  form: InternshipForm,
  context: {
    semester: number
    minSemester: number
    previewViable: boolean | null
    hasVerifiedPhone: boolean
    minGuarantorIncome: number
    responsibleCount: number
    guarantorCount: number
    hasRequiredDocs: boolean
    hasLetter: boolean
    hasProposal: boolean
    denied: boolean
  },
): boolean {
  const tuition = Number(form.tuition)
  switch (step) {
    case 'fac':
      return Boolean(
        form.collegeKey &&
          form.graduation &&
          context.semester >= context.minSemester &&
          tuition >= 3000 &&
          tuition <= 18000,
      )
    case 'sem':
      return form.startSemester > 0
    case 'divida':
      return (
        !form.hasDebt || (Number(form.debt) > 0 && Number(form.debt) <= 200000)
      )
    case 'res':
      return context.previewViable != null
    case 'eu':
      return Boolean(context.hasVerifiedPhone && form.birthdate && form.gender)
    case 'end':
      return Boolean(
        form.zip_code &&
          form.street &&
          form.number &&
          form.district &&
          form.municipality_id,
      )
    case 'civil':
      return Boolean(
        form.marital &&
          form.politically_exposed &&
          form.us_person &&
          (!isMarried(form.marital) ||
            (form.spouse_name && digits(form.spouse_tax_id).length === 11)),
      )
    case 'traj':
      return Boolean(
        form.high_school_type &&
          form.academic_award &&
          form.course_locked &&
          (form.academic_award !== 'yes' || form.academic_award_description) &&
          (form.course_locked !== 'yes' || form.course_locked_reason),
      )
    case 'planos':
      return Boolean(
        form.residency_timing &&
          (!form.plans.includes('other') || form.plans_other),
      )
    case 'fin':
      return Boolean(
        form.payment_sources.length && form.financing_reason.trim(),
      )
    case 'resp':
      return (
        form.selfResponsible === 'no' ||
        (form.selfResponsible === 'yes' && form.self_income !== '')
      )
    case 'rlist':
      return context.responsibleCount > 0
    case 'av':
      return context.guarantorCount > 0
    case 'scr':
      return form.scr
    case 'docs':
      return context.hasRequiredDocs
    case 'carta':
      return context.hasLetter
    case 'analise':
      return !context.denied
    case 'proposta':
      return context.hasProposal
    case 'assinar':
      return false
    default: {
      const role = step.startsWith('r')
        ? 'r'
        : step.startsWith('g')
          ? 'g'
          : null
      const index = Number(step.slice(1))
      if (role && index) {
        return isBackerStepValid(
          role,
          index,
          form.backer,
          context.minGuarantorIncome,
        )
      }
      return true
    }
  }
}

const storageKey = (applicationKey: string) =>
  `internship-draft:${applicationKey}`

/** Rascunho local: o que ainda não foi salvo no financial sobrevive a um recarregamento. */
export function readDraft(applicationKey: string): Partial<InternshipForm> {
  try {
    const raw = localStorage.getItem(storageKey(applicationKey))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {}
    const record = parsed as Record<string, unknown>
    const sample = emptyForm()
    const draft: Partial<InternshipForm> = {}
    for (const key of Object.keys(sample) as (keyof InternshipForm)[]) {
      const value = record[key]
      const expected = sample[key]
      if (value === undefined) continue
      if (Array.isArray(expected)) {
        if (
          Array.isArray(value) &&
          value.every((item) => typeof item === 'string')
        ) {
          Object.assign(draft, { [key]: value })
        }
        continue
      }
      if (key === 'backer') {
        if (value && typeof value === 'object') {
          Object.assign(draft, {
            backer: { ...emptyBacker(), ...(value as object) },
          })
        }
        continue
      }
      if (typeof value === typeof expected)
        Object.assign(draft, { [key]: value })
    }
    return draft
  } catch {
    return {}
  }
}

export function writeDraft(
  applicationKey: string,
  partial: Partial<InternshipForm>,
) {
  try {
    const next = { ...readDraft(applicationKey), ...partial }
    localStorage.setItem(storageKey(applicationKey), JSON.stringify(next))
  } catch {
    // sem armazenamento local, o formulário segue só em memória
  }
}

export function clearDraftKeys(
  applicationKey: string,
  keys: (keyof InternshipForm)[],
) {
  try {
    const current = readDraft(applicationKey)
    for (const key of keys) delete current[key]
    localStorage.setItem(storageKey(applicationKey), JSON.stringify(current))
  } catch {
    // nada a limpar
  }
}

export function clearDraft(applicationKey: string) {
  try {
    localStorage.removeItem(storageKey(applicationKey))
  } catch {
    // nada a limpar
  }
}
